import { useEffect, useState } from 'react';
import { apiClient } from '../api/client';

/**
 * ADR-016 M2.9 — the tenant-side face of control-plane announcements. Reads the notices the
 * platform pushed into THIS tenant's database (anonymous endpoint: it also shows on the login
 * page). Severity drives the look; Critical cannot be dismissed, others remember dismissal for
 * the session. Body is always rendered as plain text — never HTML (XSS stance set in M2).
 */
interface PlatformNoticeDto {
  kind: string;      // Notice | Maintenance | Release
  severity: string;  // Info | Warning | Critical
  title: string;
  body: string;
  startsAt: string;
  endsAt: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const SEVERITY_STYLES: Record<string, { wrap: string; tag: string; icon: string }> = {
  Info: {
    wrap: 'bg-sky-50 border-sky-300 text-sky-900',
    tag: 'bg-sky-600 text-white',
    icon: 'ℹ',
  },
  Warning: {
    wrap: 'bg-amber-50 border-amber-300 text-amber-900',
    tag: 'bg-amber-500 text-white',
    icon: '⚠',
  },
  Critical: {
    wrap: 'bg-red-50 border-red-400 text-red-900',
    tag: 'bg-red-600 text-white',
    icon: '⛔',
  },
};

const noticeKey = (n: PlatformNoticeDto) => `pn-dismissed:${n.title}:${n.startsAt}`;

interface Props {
  /** Fixed overlay for the login page; in-flow bar inside the app layout. */
  variant?: 'inline' | 'overlay';
}

export const PlatformNoticeBanner = ({ variant = 'inline' }: Props) => {
  const [notices, setNotices] = useState<PlatformNoticeDto[]>([]);
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<ApiEnvelope<PlatformNoticeDto[]>>('/platform-notices')
      .then((r) => {
        if (cancelled || !r.data?.success) return;
        setNotices(r.data.data ?? []);
        const seen: Record<string, boolean> = {};
        for (const n of r.data.data ?? []) {
          try {
            seen[noticeKey(n)] = sessionStorage.getItem(noticeKey(n)) === '1';
          } catch {
            seen[noticeKey(n)] = false;
          }
        }
        setDismissed(seen);
      })
      .catch(() => {
        /* the banner must never break a page — no notice is always a valid state */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = (n: PlatformNoticeDto) => {
    try {
      sessionStorage.setItem(noticeKey(n), '1');
    } catch {
      /* private mode etc. — dismiss still works for this render */
    }
    setDismissed((d) => ({ ...d, [noticeKey(n)]: true }));
  };

  const visible = notices.filter((n) => n.severity === 'Critical' || !dismissed[noticeKey(n)]);
  if (visible.length === 0) return null;

  return (
    <div
      className={
        variant === 'overlay'
          ? 'fixed inset-x-0 top-0 z-50 flex flex-col gap-px'
          : 'mb-4 flex flex-col gap-2'
      }
    >
      {visible.map((n) => {
        const style = SEVERITY_STYLES[n.severity] ?? SEVERITY_STYLES.Info;
        return (
          <div
            key={noticeKey(n)}
            role="status"
            className={`flex items-start gap-3 border px-4 py-2.5 text-sm ${style.wrap} ${
              variant === 'inline' ? 'rounded-lg' : ''
            }`}
          >
            <span aria-hidden className="mt-0.5 text-base leading-none">{style.icon}</span>
            <span
              className={`mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${style.tag}`}
            >
              {n.kind}
            </span>
            <div className="min-w-0 flex-1">
              <span className="font-semibold">{n.title}</span>
              {n.body && (
                <span className="ml-2 whitespace-pre-wrap break-words opacity-90">{n.body}</span>
              )}
            </div>
            {n.severity !== 'Critical' && (
              <button
                type="button"
                aria-label="Dismiss notice"
                onClick={() => dismiss(n)}
                className="ml-2 shrink-0 font-bold opacity-50 transition-opacity hover:opacity-100"
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
